import { useLocation, useNavigate } from 'react-router-dom';
import { useSavedNames } from '../saved/savedListUtils';
import { useTracking } from '../../hooks/useTracking';
import { useUiChromeStore } from '../../stores/uiChromeStore';

/** Floating price report CTA — appears once the renter has at least one saved apartment */
export function TourCartFloat() {
  const savedCount = useSavedNames().length;
  const navigate = useNavigate();
  const location = useLocation();
  const { track } = useTracking();
  const floatsSuppressed = useUiChromeStore(s => s.floatsSuppressed);

  // Visible for anonymous renters too (CONTEXT.md: appears at ≥1 save) —
  // checkout is where the account gets created now (plan 2026-07-07 §4).
  // Stands down while a flow owns the bottom edge (wizard footer), on
  // /my-list (its header already carries the Get Price Report CTA — the
  // FAB was a duplicate that overlapped card content), and on
  // /price-report (the FAB's own destination).
  const onOwnSurface =
    location.pathname.startsWith('/my-list') ||
    location.pathname.startsWith('/price-report');
  // Admin tables own the bottom edge; renter checkout must not cover their controls.
  const onAdmin = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  if (savedCount === 0 || floatsSuppressed || onOwnSurface || onAdmin) return null;
  // /vibe mobile: the sticky chat input + suggestion row own the bottom
  // ~150px — float above them instead of on top of them.
  const onVibe = location.pathname.startsWith('/vibe');

  // Compact taco FAB — goes DIRECTLY to the price-report checkout (per
  // CONTEXT.md spec + plan R2-2). The checkout cart has per-row remove, so
  // no intermediate selection screen: any screen → FAB → form → submit.
  return (
    <button
      onClick={() => {
        track('checkout_open', 'fab', { saved_count: savedCount });
        navigate('/price-report');
      }}
      className={`fixed ${onVibe ? 'bottom-40' : 'bottom-20'} md:bottom-6 right-4 z-50 hover:scale-105 active:scale-95 transition-all rounded-full h-12 w-12 shadow-lg flex items-center justify-center`}
      style={{ background: 'var(--ts-accent-primary)', color: 'var(--ts-accent-contrast)' }}
      aria-label={`${savedCount} saved. Get your price report`}
      data-testid="price-report-float"
    >
      <span className="text-xl leading-none" aria-hidden>🌮</span>
      <span
        className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full text-[11px] font-bold text-[color:var(--ts-bg-base)]"
        style={{ background: 'var(--ts-text-primary)' }}
      >
        {savedCount}
      </span>
    </button>
  );
}
